<script lang="ts">
    import { onMount } from 'svelte'
    import DateSelect from '../../_common/date-select.svelte'
    import { GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Grid from '../../_common/z/zgrid.svelte'
    import type { Appointment } from '../../../modules/schedulerApi/appointments/appointment'
    import { AppointmentStatus } from '../../../modules/schedulerApi/appointments/appointments.enum'
    import type { Schedule } from '../../../modules/schedulerApi/schedules/schedule'
    import LoadSpinner from '../../_common/load-spinner.svelte'

    // status que efetivamente ocupam o horário na agenda (cancelamentos e bloqueios liberam o slot) — mesma regra de dashboard-agendas.svelte
    const OCCUPYING_STATUS = [
        AppointmentStatus.agendamento_criado,
        AppointmentStatus.agendamento_confirmado,
        AppointmentStatus.agendamento_compareceu,
        AppointmentStatus.agendamento_faltou,
        AppointmentStatus.agendamento_remarcado,
        AppointmentStatus.agendamento_remarcado_descontar,
    ]

    let dateSelect: { from: Date; to: Date } = window.api.utils.dates.today()

    onMount(() => {
        load(dateSelect.from, dateSelect.to)
    })

    let show = false
    let grid: GridClass = null

    async function load(from: Date, to: Date) {
        if (show) return
        show = true
        const [appointmentsRaw, schedulesRaw]: [Appointment[], Schedule[]] = await Promise.all([
            window.api.schedulers.appointments.getAll({ from, to }),
            window.api.schedulers.schedules.getAll({ discardSlots: false }),
        ])
        const schedules = schedulesRaw.filter((s) => s.status === 100)
        console.log('relat.data', appointmentsRaw, schedules)
        loadGrid(appointmentsRaw, schedules, from, to)
        show = false
    }

    // soma, em minutos, a janela de funcionamento configurada em "slots" para cada dia do período
    function scheduleCapacityMinutes(schedule: Schedule, from: Date, to: Date) {
        let total = 0
        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
            const dayConfig = schedule.slots?.[d.getDay()]
            if (!dayConfig) continue
            for (const slot of dayConfig.slots || []) {
                if (slot.active) total += slot.end - slot.start
            }
        }
        return total
    }

    // funde intervalos sobrepostos (encaixes na mesma agenda) para não contar o tempo em paralelo 2x
    function mergeIntervals(intervals: { start: number; end: number }[]) {
        if (intervals.length === 0) return []
        const sorted = [...intervals].sort((a, b) => a.start - b.start)
        let merged: { start: number; end: number }[] = [{ ...sorted[0] }]
        for (let i = 1; i < sorted.length; i++) {
            const last = merged[merged.length - 1]
            const current = sorted[i]
            if (current.start <= last.end) {
                last.end = Math.max(last.end, current.end)
            } else {
                merged.push({ ...current })
            }
        }
        return merged
    }

    function occupiedMinutes(appointments: Appointment[], scheduleId: string) {
        const intervals = appointments
            .filter((a) => a.scheduleId === scheduleId && OCCUPYING_STATUS.includes(a.status))
            .map((a) => ({ start: new Date(a.start).getTime(), end: new Date(a.end).getTime() }))
            .filter((i) => i.end > i.start)
        const merged = mergeIntervals(intervals)
        return merged.reduce((total, i) => total + (i.end - i.start) / 60000, 0)
    }

    function loadGrid(appointments: Appointment[], schedules: Schedule[], from: Date, to: Date) {
        grid = new GridClass()
        grid.title = 'Taxa de ocupação'
        grid.cols.push(new GridCol({ content: 'Agenda' }))
        grid.cols.push(new GridCol({ content: 'Horas disponíveis', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: 'Horas em atendimento', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: 'Taxa de ocupação', type: GridColTypeEnum.percent }))

        const rows = schedules
            .map((schedule) => {
                const capacityMinutes = scheduleCapacityMinutes(schedule, from, to)
                const occupied = occupiedMinutes(appointments, schedule.scheduleId)
                return {
                    label: schedule.label,
                    capacityHours: capacityMinutes / 60,
                    occupiedHours: occupied / 60,
                    rate: capacityMinutes > 0 ? (occupied / capacityMinutes) * 100 : null,
                }
            })
            // agendas sem horário de funcionamento configurado (0 horas disponíveis) não aparecem, mesmo critério de dashboard-agendas.svelte
            .filter((row) => row.rate !== null)
            .sort((a, b) => b.rate - a.rate)

        for (const row of rows) {
            let line: GridLine = new GridLine()
            line.slots.push({ content: row.label })
            line.slots.push({ content: row.capacityHours })
            line.slots.push({ content: row.occupiedHours })
            line.slots.push({ content: row.rate })
            grid.lines.push(line)
        }
        console.log('relat.grid', grid)
        grid = grid
    }

    function dateChanged(event) {
        dateSelect = event.detail
        load(dateSelect.from, dateSelect.to)
    }
</script>

<div class="relat-body">
    <div class="acts h-print">
        <div class="date">
            <DateSelect on:change={dateChanged} />
        </div>
    </div>
    <div class="grid mt-2">
        <Grid {grid} />
    </div>
</div>
<LoadSpinner {show}></LoadSpinner>
